import { createI18n } from 'vue-i18n'
import { readString, writeString } from '../lib/storage'
import { resolveLocale, systemLanguages } from './detect.js'
import { LOCALE_LOADERS } from './loaders.js'
import type { LocaleCode } from './locales.js'
import { DEFAULT_LOCALE, isSupportedLocale } from './locales.js'
import { en } from './messages/en.js'
import { PLURAL_RULES } from './plurals.js'

export type { LocaleCode } from './locales.js'
export { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './locales.js'

const STORAGE_KEY = 'songster.locale'

/**
 * English is bundled: it is the fallback for any key a translation has not
 * caught up with, so it must always be present.
 */
/** Typed by locale code so `locale.value` accepts any supported language. */
export const messages: Partial<Record<LocaleCode, typeof en>> = { en }

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: DEFAULT_LOCALE,
  fallbackLocale: DEFAULT_LOCALE,
  // A key missing from a translation falls back silently rather than shouting
  // in the console on every render.
  missingWarn: false,
  fallbackWarn: false,
  messages,
  pluralRules: PLURAL_RULES,
})

const loaded = new Set<LocaleCode>([DEFAULT_LOCALE])

/** The language the user explicitly chose, if any. */
export function storedLocale(): string | null {
  return readString(STORAGE_KEY)
}

/**
 * Switches language, fetching the catalog first if it has not been loaded.
 * Falls back to English if a catalog cannot be fetched — an offline PWA asked
 * for a language it has never downloaded should still be usable.
 */
export async function setLocale(code: LocaleCode): Promise<LocaleCode> {
  if (!loaded.has(code)) {
    const load = LOCALE_LOADERS[code as Exclude<LocaleCode, 'en'>]
    try {
      const module = await load()
      i18n.global.setLocaleMessage(code, module.default as never)
      loaded.add(code)
    } catch {
      return DEFAULT_LOCALE
    }
  }
  i18n.global.locale.value = code
  writeString(STORAGE_KEY, code)
  if (typeof document !== 'undefined') document.documentElement.lang = code
  return code
}

/**
 * Applies the language to use on start: an explicit choice if there is one,
 * otherwise the best match for the system languages, otherwise English.
 */
export async function initLocale(): Promise<LocaleCode> {
  const code = resolveLocale({
    stored: storedLocale(),
    systemLanguages: systemLanguages(),
  })
  if (code === DEFAULT_LOCALE) {
    if (typeof document !== 'undefined') document.documentElement.lang = DEFAULT_LOCALE
    return DEFAULT_LOCALE
  }
  return setLocale(code)
}

/** Used by the switcher to show which entry is active. */
export function currentLocale(): LocaleCode {
  const value = String(i18n.global.locale.value)
  return isSupportedLocale(value) ? value : DEFAULT_LOCALE
}
